import {
  divWithStyle,
  elideTextStyle,
  Style,
  ViewerPaneObfuscatedPlaceholder,
} from '@tableau/data-catalog-shared';
import { ExpandoDirection, ExpandoWidget } from '@tableau/widgets';
import * as React from 'react';
import { style } from 'typestyle';
import { SvgFieldIcon } from '../field/components/SvgFieldIcon';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldNameMappings } from '../field/functions/FieldNameMapping';
import { SheetField } from './SheetField';
import { SheetFieldListElementContainer } from './ViewerPaneStyles';
import { VizClientFieldCard } from './VizClientFieldCard';

const FieldIconHeight = 17;

export interface VizClientFieldElementProps {
  readonly field: SheetField;
  readonly workbookVizportalUrlId?: string;
}

interface VizClientFieldElementState {
  readonly expanded: boolean;
}

// this is a workaround since the shared widgets expando doesn't have a prop that allows us to right
// align the carat, note that this breaks the contract with shared widgets, so if a bug occurs as a
// result of the widget being updated, its on us to fix it
const expandoCaratRightAlignedStyle: Style = {
  $nest: {
    '& > [role="button"]': {
      width: '100%'
    },
    '& > [role="button"] > div': {
      flexGrow: 1
    },
    '& > [role="button"] > img': {
      order: 1
    },
  },
};

const FieldLabel = divWithStyle('VizClientFieldLabel', {
  ...elideTextStyle,
  maxWidth: 225 // TODO: try to remove hardcoded width (makes eliding work)
});

const FieldWrapper = divWithStyle('VizClientFieldWrapper', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const FieldCardFieldIcon = divWithStyle('VizClientFieldCardFieldIcon', {
  flexShrink: 0,
  height: FieldIconHeight,
});

export class VizClientFieldElement extends React.Component<VizClientFieldElementProps, VizClientFieldElementState> {
  public constructor(props: VizClientFieldElementProps) {
    super(props);
    this.state = { expanded: false };
  }

  private renderIconWithFieldName() {
    const fieldIcon = iconForField(this.props.field);
    return (
      <FieldWrapper>
        <FieldCardFieldIcon title={fieldIcon ? FieldNameMappings[fieldIcon]() : ''}>
          <SvgFieldIcon fieldIcon={fieldIcon} />
        </FieldCardFieldIcon>
        <FieldLabel>{this.props.field.name}</FieldLabel>
      </FieldWrapper>
    );
  }

  public render(): JSX.Element {
    const isObfuscated: boolean = this.props.field.name === null;
    // the expando widget always will render its children, and hide it with 'display: hidden',
    // because of that its important that we make this check when rendering its children, otherwise
    // we may end up issuing way too many graphql requests up front, since some of the fields have
    // card content that issues its own graphql query
    const shouldRenderVizClientFieldCard = this.state.expanded;
    return (
      <SheetFieldListElementContainer data-tb-test-id='data-details-field-element-container'>
        {isObfuscated ? ViewerPaneObfuscatedPlaceholder() :
          <ExpandoWidget
            className={style(expandoCaratRightAlignedStyle)}
            direction={ExpandoDirection.Down}
            isExpanded={this.state.expanded}
            onCollapse={() => this.setState({ expanded: false })}
            onExpand={() => this.setState({ expanded: true })}
            testId={'data-details-field-element-expando'}
            style={{ display: 'block' }} // so expando content fills width
            label={this.renderIconWithFieldName()}
          >
            {shouldRenderVizClientFieldCard ? (
              <VizClientFieldCard
                field={this.props.field}
                workbookVizportalUrlId={this.props.workbookVizportalUrlId}
              />
            ) : null}
          </ExpandoWidget>
        }
      </SheetFieldListElementContainer>
    );
  }
}

